import React from "react";
import { ChevronLeft, ChevronRight, ThreeDots } from "react-bootstrap-icons";
import { TFunction } from "i18next";

type PaginationProps = {
  t: TFunction;
  pages: number;
  currentPage: number;
  firstItemIndex: number;
  lastItemIndex: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  limit: number;
  onLimitChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
};

const Pagination: React.FC<PaginationProps> = ({
  t,
  pages,
  currentPage,
  onPageChange,
  firstItemIndex,
  lastItemIndex,
  totalItems,
  limit,
  onLimitChange,
}) => {
  const renderPageNumbers = () => {
    const maxPagesToShow = 3;
    const pageNumbers = [];

    if (pages <= maxPagesToShow) {
      for (let i = 1; i <= pages; i++) {
        pageNumbers.push(
          <li
            key={i}
            className={`page-item ${i === currentPage ? "active" : ""}`}
          >
            <button
              className="page-link z-1 border-0 rounded"
              onClick={() => onPageChange(i)}
            >
              {i}
            </button>
          </li>
        );
      }
    } else {
      const startPage = Math.max(
        1,
        currentPage - Math.floor(maxPagesToShow / 2)
      );
      const endPage = Math.min(pages, startPage + maxPagesToShow - 1);

      if (startPage > 1) {
        pageNumbers.push(
          <li key={1} className="page-item">
            <button
              className="page-link z-1 ms-2"
              onClick={() => onPageChange(1)}
            >
              1
            </button>
          </li>
        );
        if (startPage > 2) {
          pageNumbers.push(
            <li key={"ellipsis-start"} className="page-item disabled">
              <span className="page-link z-1 ms-1">
                <ThreeDots className="text-dark" />
              </span>
            </li>
          );
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
          <li
            key={i}
            className={`page-item ${i === currentPage ? "active" : ""}`}
          >
            <button
              className="page-link z-1 ms-2 me-1"
              onClick={() => onPageChange(i)}
            >
              {i}
            </button>
          </li>
        );
      }

      if (endPage < pages) {
        if (endPage < pages - 1) {
          pageNumbers.push(
            <li key={"ellipsis-end"} className="page-item disabled">
              <span className="page-link z-1 ms-0">
                <ThreeDots className="text-dark" />
              </span>
            </li>
          );
        }
        pageNumbers.push(
          <li key={pages} className="page-item">
            <button
              className="page-link z-1 ms-1 me-1"
              onClick={() => onPageChange(pages)}
            >
              {pages}
            </button>
          </li>
        );
      }
    }

    return pageNumbers;
  };

  return (
    <>
      <div className="d-none d-md-flex justify-content-between align-items-center py-3 mt-4">
        <div>
          <select
            className="form-select border-0 py-0 ps-0 bg-transparent fs-7 text-gray"
            value={limit}
            onChange={onLimitChange ?? (() => {})}
          >
            <option value={12}>12 / {t("review.pagination")}</option>
            <option value={24}>24 / {t("review.pagination")}</option>
            <option value={36}>36 / {t("review.pagination")}</option>
          </select>
        </div>
        <nav>
          <ul className="pagination mb-0">
            <li className={`page-item  ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link d-flex align-items-center h-100 me-1 border-0"
                onClick={() => onPageChange(currentPage - 1)}
              >
                <ChevronLeft className="page-link-arrow" />
              </button>
            </li>
            {renderPageNumbers()}
            <li
              className={`page-item  ${currentPage === pages ? "disabled" : ""}`}
            >
              <button
                className="page-link d-flex align-items-center h-100 border-0"
                onClick={() => onPageChange(currentPage + 1)}
              >
                <ChevronRight className="page-link-arrow" />
              </button>
            </li>
          </ul>
        </nav>
        <div>
          <span className="text-gray fs-7">
            {firstItemIndex}-{lastItemIndex} {t("of")} {totalItems}
          </span>
        </div>
      </div>
      <div className="d-flex flex-column d-md-none p-3 mt-3 mt-md-5 rounded">
        <nav>
          <ul className="pagination d-flex justify-content-center mb-2">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link d-flex align-items-center h-100 me-1 border-0"
                onClick={() => onPageChange(currentPage - 1)}
              >
                <ChevronLeft className="page-link-arrow" />
              </button>
            </li>
            {renderPageNumbers()}
            <li
              className={`page-item  ${currentPage === pages ? "disabled" : ""}`}
            >
              <button
                className="page-link d-flex align-items-center h-100 ms-1  border-0"
                onClick={() => onPageChange(currentPage + 1)}
              >
                <ChevronRight className="page-link-arrow" />
              </button>
            </li>
          </ul>
        </nav>
        <div className="col-12 d-flex align-items-center justify-content-between mt-4">
          <div>
            <select
              className="form-select border-0 bg-transparent py-0 ps-0 text-gray fs-7"
              value={limit}
              onChange={onLimitChange ?? (() => {})}
            >
              <option value={12}>12 / {t("review.pagination")}</option>
              <option value={24}>24 / {t("review.pagination")}</option>
              <option value={36}>36 / {t("review.pagination")}</option>
            </select>
          </div>
          <div>
            <span className="text-gray fs-7">
              {firstItemIndex}-{lastItemIndex} {t("of")} {totalItems}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Pagination;
